import React, { useCallback, useEffect, useState } from 'react';
import Box from '@components/Atoms/Box';
import PassageCreation from '@learnosityassessment/Components/PassageCreation';
import PreviewPassageItems from '@learnosityassessment/Components/PreviewPassageItems';
import ChalkTalkErrorBoundary from '@components/ChalkTalkErrorBoundary';
import ShowIf from '@components/Atoms/ShowIf';
import { CircularProgress, Typography } from '@mui/material';
import { useUser } from '@reducers/user/hooks';
import { Passage } from './types';
import PassageHeader from './PassageHeader';
import PassageList, { PassageSource } from './PassageList';
import { useGetPassage } from './hooks';

export const MODES = {
  LIST_MODE: 'list',
  PASSAGE_MODE: 'passage',
  PASSAGE_ITEMS_MODE: 'items',
  PASSAGE_SELECT_MODE: 'select',
  PASSAGE_GENERATE_MODE: 'generate',
};

const Passages: React.FC<{
  selectPassageCallback: (passage: Passage) => void;
  onCloseCallback: () => void;
  inDialog?: boolean;
}> = ({ selectPassageCallback, onCloseCallback, inDialog = false }) => {
  const user = useUser();
  const [selectedPassage, setSelectedPassage] = useState<Passage | null | string>(null);
  const [mode, setMode] = useState(MODES.LIST_MODE);
  const [passageEditorReady, setPassageEditorReady] = useState(false);
  const { data: selectedPassageObject } = useGetPassage(selectedPassage);
  const [passageSource, setPassageSource] = useState<string>(
    PassageSource.CHALKTALK_PASSAGES,
  );
  const [currentPage, setCurrentPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [readability, setReadability] = useState<string[]>([]);
  const [fleschgrade, setFleschgrade] = useState<string[]>([]);

  useEffect(() => {
    if (
      !(typeof selectedPassage === 'string') ||
      !(typeof selectedPassageObject === 'object')
    ) {
      return;
    }

    setSelectedPassage(selectedPassageObject);
    if (mode === MODES.PASSAGE_SELECT_MODE) {
      selectPassageCallback(selectedPassageObject as Passage);
      if (inDialog) {
        onCloseCallback();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedPassageObject,
    selectedPassage,
    selectPassageCallback,
    inDialog,
    onCloseCallback,
  ]);

  const switchMode = (toMode: string, passage: Passage | null | string = null) => {
    setPassageEditorReady(false);
    setSelectedPassage(passage);
    setMode(toMode);
  };

  const isPassageReadOnly = useCallback(() => {
    if (typeof selectedPassage !== 'object') {
      return true;
    }
    if (selectedPassage?.data?.metadata?.author_id?.toString() !== user?.id?.toString()) {
      return true;
    }
    return false;
  }, [selectedPassage, user]);

  const renderMode = () => {
    switch (mode) {
      case MODES.PASSAGE_MODE:
      case MODES.PASSAGE_GENERATE_MODE:
        return (
          <Box sx={{ height: '100%', width: '100%' }}>
            <ShowIf If={!passageEditorReady}>
              <Box sx={{ height: '5rem', width: '100%', textAlign: 'center' }}>
                <Typography variant="h3" sx={{ mt: '2rem', mb: '1rem' }}>
                  Loading Passage...
                </Typography>
                <CircularProgress size="6rem" />
              </Box>
            </ShowIf>

            <ShowIf If={!selectedPassage || typeof selectedPassage === 'object'}>
              <PassageCreation
                passage={selectedPassage}
                switchMode={switchMode}
                isGenerateMode={mode === MODES.PASSAGE_GENERATE_MODE}
                onRender={() => setPassageEditorReady(true)}
                readOnly={isPassageReadOnly()}
              />
            </ShowIf>
          </Box>
        );

      case MODES.PASSAGE_ITEMS_MODE:
        return <PreviewPassageItems passage={selectedPassage as Passage} />;

      case MODES.PASSAGE_SELECT_MODE:
        return (
          <Box sx={{ mt: '5rem', width: '100%', textAlign: 'center' }}>
            <Typography variant="h3" sx={{ mt: '2rem', mb: '1rem' }}>
              Selecting Passage...
            </Typography>
            <CircularProgress size="6rem" />
          </Box>
        );

      default:
        return (
          <PassageList
            switchMode={switchMode}
            inDialog={inDialog}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            readability={readability}
            setReadability={setReadability}
            fleschgrade={fleschgrade}
            setFleschgrade={setFleschgrade}
            passageSource={passageSource}
            setPassageSource={setPassageSource}
          />
        );
    }
  };

  return (
    <Box
      sx={{
        height: inDialog ? '90%' : '75%',
        width: '100%',
        pl: '1rem',
      }}
    >
      <ChalkTalkErrorBoundary>
        <ShowIf If={!inDialog}>
          <PassageHeader
            mode={mode}
            switchMode={switchMode}
            close={onCloseCallback}
            inDialog={inDialog}
          />
        </ShowIf>
        {renderMode()}
      </ChalkTalkErrorBoundary>
    </Box>
  );
};

export default Passages;
